import React, { useState, useEffect } from 'react';

const toArray = (value: any): any[] => (Array.isArray(value) ? value : []);
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Autocomplete, LinearProgress, Tooltip, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, ExpandMore as ExpandIcon,
  CheckCircle as CompleteIcon, PlayArrow as StartIcon,
  RemoveCircleOutline as RemoveIcon, AddCircleOutline as AddItemIcon,
  Assignment as PlanIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const statusLabels: Record<string, string> = {
  draft: 'Rascunho', proposed: 'Proposto', approved: 'Aprovado',
  in_progress: 'Em Andamento', paused: 'Pausado', completed: 'Concluído', cancelled: 'Cancelado',
};
const statusColors: Record<string, string> = {
  draft: '#94a3b8', proposed: '#3b82f6', approved: '#8b5cf6',
  in_progress: '#14B8A6', paused: '#f59e0b', completed: '#22c55e', cancelled: '#ef4444',
};
const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const TreatmentPlan: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [plans, setPlans] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewPlan, setViewPlan] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });

  const [form, setForm] = useState<any>({
    patient_id: '', professional_id: '', title: '', description: '',
    priority: 'normal', start_date: '', estimated_end_date: '', notes: '',
    phases: [{ title: 'Fase 1', description: '', color: '#14B8A6', items: [] }],
  });
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const [procOptions, setProcOptions] = useState<any[]>([]);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadPlans(); }, [search, filterStatus]);

  useEffect(() => {
    if (openDialog) {
      searchPatients('');
      searchProfessionals('');
      searchProcedures('');
    }
  }, [openDialog]);

  const loadData = async () => { await Promise.all([loadPlans(), loadStats()]); };

  const loadPlans = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50 };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      const { data } = await axios.get(getApiUrl('/clinic/treatment-plans-enhanced'), { params });
      setPlans(toArray(data?.data ?? data));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/treatment-plans-enhanced/stats'));
      setStats((data?.data ?? data) || {});
    } catch (e) { console.error(e); }
  };

  const searchPatients = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/prescriptions-search/patients'), { params: { search: q } });
      setPatientOptions(toArray(data?.data ?? data));
    } catch {
      setPatientOptions([]);
    }
  };

  const searchProfessionals = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/prescriptions-search/professionals'), { params: { search: q } });
      setProfOptions(toArray(data?.data ?? data));
    } catch {
      setProfOptions([]);
    }
  };

  const searchProcedures = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/budgets-search/procedures'), { params: { search: q } });
      setProcOptions(toArray(data?.data ?? data));
    } catch {
      setProcOptions([]);
    }
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(getApiUrl(`/clinic/treatment-plans-enhanced/${editing.id}`), form);
        setSnackbar({ open: true, message: 'Plano atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/treatment-plans-enhanced'), form);
        setSnackbar({ open: true, message: 'Plano criado!', severity: 'success' });
      }
      setOpenDialog(false); resetForm(); loadData();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir plano?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/treatment-plans-enhanced/${id}`));
      setSnackbar({ open: true, message: 'Excluído!', severity: 'success' }); loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handleViewPlan = async (id: string) => {
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/treatment-plans-enhanced/${id}`));
      setViewPlan(data?.data ?? data);
    } catch (e) { console.error(e); }
  };

  const handleItemStatusChange = async (itemId: string, status: string) => {
    try {
      await axios.patch(getApiUrl(`/clinic/treatment-plan-items/${itemId}/status`), { status });
      setSnackbar({ open: true, message: 'Status atualizado!', severity: 'success' });
      if (viewPlan) handleViewPlan(viewPlan.id);
      loadData();
    } catch (e) { console.error(e); }
  };

  const resetForm = () => {
    setForm({
      patient_id: '', professional_id: '', title: '', description: '',
      priority: 'normal', start_date: '', estimated_end_date: '', notes: '',
      phases: [{ title: 'Fase 1', description: '', color: '#14B8A6', items: [] }],
    });
    setPatientOptions([]); setProfOptions([]);
  };

  const addPhase = () => {
    setForm({ ...form, phases: [...form.phases, { title: `Fase ${form.phases.length + 1}`, description: '', color: '#3b82f6', items: [] }] });
  };

  const removePhase = (idx: number) => {
    setForm({ ...form, phases: form.phases.filter((_: any, i: number) => i !== idx) });
  };

  const addItemToPhase = (phaseIdx: number, proc: any) => {
    const phases = [...form.phases];
    phases[phaseIdx].items.push({
      procedure_id: proc?.id || '', procedure_name: proc?.name || '', procedure_code: proc?.code || '',
      quantity: 1, unit_price: proc?.price || 0, tooth_number: '', notes: '',
    });
    setForm({ ...form, phases });
  };

  const removeItemFromPhase = (phaseIdx: number, itemIdx: number) => {
    const phases = [...form.phases];
    phases[phaseIdx].items.splice(itemIdx, 1);
    setForm({ ...form, phases });
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Plano de Tratamento</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Crie e acompanhe planos de tratamento por fases</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadData} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }} onClick={() => { setEditing(null); resetForm(); setOpenDialog(true); }}>
            Novo Plano
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: stats.total || 0, color: '#14B8A6' },
          { label: 'Em Andamento', value: stats.in_progress || 0, color: '#3b82f6' },
          { label: 'Concluídos', value: stats.completed || 0, color: '#22c55e' },
          { label: 'Rascunho', value: stats.draft || 0, color: '#94a3b8' },
        ].map((kpi, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ bgcolor: 'white', borderRadius: 3, border: `1px solid ${kpi.color}22` }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>{kpi.label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2, '&:last-child': { pb: 2 } }}>
          <TextField size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ minWidth: 250 }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(statusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600 }}>Nº</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Título</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Progresso</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : plans.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhum plano</TableCell></TableRow>
              ) : plans.map((plan) => (
                <TableRow key={plan.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewPlan(plan.id)}>
                  <TableCell sx={{ fontWeight: 600, color: '#14B8A6' }}>{plan.plan_number || '-'}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{plan.title || '-'}</TableCell>
                  <TableCell>{plan.patient_name || '-'}</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress variant="determinate" value={plan.progress_percentage || 0}
                        sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#14B8A6', borderRadius: 4 } }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#14B8A6', minWidth: 35 }}>{Math.round(plan.progress_percentage || 0)}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(plan.total_amount)}</TableCell>
                  <TableCell><Chip label={statusLabels[plan.status] || plan.status} size="small"
                    sx={{ bgcolor: (statusColors[plan.status] || '#666') + '15', color: statusColors[plan.status] || '#666', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => handleDelete(plan.id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* View Plan Dialog */}
      <Dialog open={!!viewPlan} onClose={() => setViewPlan(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{viewPlan?.title || 'Plano de Tratamento'}</Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>{viewPlan?.plan_number} | {viewPlan?.patient?.full_name || ''}</Typography>
          </Box>
          <IconButton onClick={() => setViewPlan(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewPlan && (
            <>
              <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={statusLabels[viewPlan.status] || viewPlan.status}
                  sx={{ bgcolor: (statusColors[viewPlan.status] || '#666') + '15', color: statusColors[viewPlan.status], fontWeight: 600 }} />
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Progresso: <strong>{Math.round(viewPlan.progress_percentage || 0)}%</strong>
                </Typography>
                <Typography>Total: <strong>{formatCurrency(viewPlan.total_amount)}</strong></Typography>
              </Box>

              <LinearProgress variant="determinate" value={viewPlan.progress_percentage || 0}
                sx={{ height: 12, borderRadius: 6, mb: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#14B8A6', borderRadius: 6 } }} />

              {(viewPlan.phases || []).map((phase: any, idx: number) => (
                <Accordion key={phase.id || idx} defaultExpanded sx={{ mb: 1, borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandIcon />}
                    sx={{ bgcolor: phase.color ? `${phase.color}10` : '#f8fafc', borderRadius: '12px', '&.Mui-expanded': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: phase.color || '#14B8A6' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{phase.title}</Typography>
                      <Chip label={`${phase.completed_items || 0}/${phase.total_items || 0}`} size="small" sx={{ ml: 'auto', mr: 1, fontWeight: 600, bgcolor: '#14B8A615', color: '#14B8A6' }} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Procedimento</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Dente</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Ação</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(phase.items || []).map((item: any) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.procedure_name}</TableCell>
                            <TableCell>{item.tooth_number || '-'}</TableCell>
                            <TableCell>{formatCurrency(item.total_price)}</TableCell>
                            <TableCell>
                              <Chip label={item.status === 'completed' ? 'Concluído' : item.status === 'in_progress' ? 'Em Andamento' : 'Pendente'} size="small"
                                sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: item.status === 'completed' ? '#dcfce7' : item.status === 'in_progress' ? '#dbeafe' : '#f1f5f9', color: item.status === 'completed' ? '#22c55e' : item.status === 'in_progress' ? '#3b82f6' : '#94a3b8' }} />
                            </TableCell>
                            <TableCell align="right">
                              {item.status !== 'completed' && (
                                <Tooltip title="Marcar como concluído">
                                  <IconButton size="small" onClick={() => handleItemStatusChange(item.id, 'completed')} sx={{ color: '#22c55e' }}>
                                    <CompleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Plan Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{editing ? 'Editar' : 'Novo'} Plano de Tratamento</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Autocomplete size="small" options={patientOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchPatients(v)} onChange={(_, v) => setForm({ ...form, patient_id: v?.id || '' })}
                onOpen={() => searchPatients('')} renderInput={(params) => <TextField {...params} label="Paciente" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete size="small" options={profOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchProfessionals(v)} onChange={(_, v) => setForm({ ...form, professional_id: v?.id || '' })}
                onOpen={() => searchProfessionals('')} renderInput={(params) => <TextField {...params} label="Profissional" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Prioridade</InputLabel>
                <Select value={form.priority} label="Prioridade" onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <MenuItem value="low">Baixa</MenuItem><MenuItem value="normal">Normal</MenuItem><MenuItem value="high">Alta</MenuItem><MenuItem value="urgent">Urgente</MenuItem>
                </Select></FormControl>
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Fases do Tratamento</Typography>
          {form.phases.map((phase: any, phaseIdx: number) => (
            <Card key={phaseIdx} sx={{ mb: 2, borderRadius: 2, border: `2px solid ${phase.color || '#14B8A6'}22` }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField size="small" label="Nome da Fase" value={phase.title}
                    onChange={(e) => { const p = [...form.phases]; p[phaseIdx].title = e.target.value; setForm({ ...form, phases: p }); }} sx={{ flex: 1 }} />
                  <input type="color" value={phase.color || '#14B8A6'} onChange={(e) => { const p = [...form.phases]; p[phaseIdx].color = e.target.value; setForm({ ...form, phases: p }); }} style={{ width: 40, height: 38, border: 'none', cursor: 'pointer' }} />
                  {form.phases.length > 1 && (
                    <IconButton size="small" onClick={() => removePhase(phaseIdx)} sx={{ color: '#ef4444' }}><RemoveIcon /></IconButton>
                  )}
                </Box>

                {phase.items.map((item: any, itemIdx: number) => (
                  <Box key={itemIdx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ minWidth: 30, fontWeight: 600, color: '#64748b' }}>{itemIdx + 1}.</Typography>
                    <TextField size="small" label="Procedimento" value={item.procedure_name} sx={{ flex: 2 }}
                      onChange={(e) => { const p = [...form.phases]; p[phaseIdx].items[itemIdx].procedure_name = e.target.value; setForm({ ...form, phases: p }); }} />
                    <TextField size="small" label="Dente" value={item.tooth_number} sx={{ width: 80 }}
                      onChange={(e) => { const p = [...form.phases]; p[phaseIdx].items[itemIdx].tooth_number = e.target.value; setForm({ ...form, phases: p }); }} />
                    <TextField size="small" type="number" label="Valor" value={item.unit_price} sx={{ width: 100 }}
                      onChange={(e) => { const p = [...form.phases]; p[phaseIdx].items[itemIdx].unit_price = parseFloat(e.target.value) || 0; setForm({ ...form, phases: p }); }} />
                    <IconButton size="small" onClick={() => removeItemFromPhase(phaseIdx, itemIdx)} sx={{ color: '#ef4444' }}><RemoveIcon fontSize="small" /></IconButton>
                  </Box>
                ))}

                <Box sx={{ mt: 1 }}>
                  <Autocomplete size="small" options={procOptions} getOptionLabel={(o: any) => o.name || ''}
                    onInputChange={(_, v) => searchProcedures(v)} onChange={(_, v) => { if (v) addItemToPhase(phaseIdx, v); }}
                    onOpen={() => searchProcedures('')} value={null}
                    renderInput={(params) => <TextField {...params} label="Adicionar procedimento" placeholder="Buscar..." />}
                    isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
                </Box>
              </CardContent>
            </Card>
          ))}
          <Button startIcon={<AddIcon />} onClick={addPhase} sx={{ color: '#14B8A6' }}>Adicionar Fase</Button>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}>
            {editing ? 'Atualizar' : 'Criar Plano'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TreatmentPlan;
